import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTeamRole } from "@/hooks/useTeamRole";
import { logActivity } from "@/lib/staffActivity";
import { toast } from "sonner";
import type { TeamMember } from "@/hooks/useTeamMembers";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fromUserId: string;
  fromName: string;
  leadIds: string[];          // leads currently assigned to fromUserId
  members: TeamMember[];      // candidate new assignees
  onReassigned?: () => void;
}

export default function BulkReassignModal({
  open, onOpenChange, fromUserId, fromName, leadIds, members, onReassigned,
}: Props) {
  const { user } = useAuth();
  const { agencyId } = useTeamRole();
  const [target, setTarget] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const candidates = useMemo(
    () => members.filter(m => m.id !== fromUserId && m.invite_accepted),
    [members, fromUserId],
  );

  async function handleReassign() {
    if (!target || leadIds.length === 0 || !user || !agencyId) return;
    setSaving(true);
    try {
      const { error: updErr } = await supabase
        .from("leads")
        .update({ assigned_to: target })
        .in("id", leadIds);
      if (updErr) throw updErr;

      const rows = leadIds.map(id => ({
        lead_id: id,
        assigned_to: target,
        assigned_by: user.id,
        note: `Bulk reassigned from ${fromName}`,
      }));
      const { error: insErr } = await supabase.from("lead_assignments").insert(rows);
      if (insErr) throw insErr;

      leadIds.forEach(id => logActivity(user.id, agencyId, "lead_assigned", id));

      const targetName = candidates.find(c => c.id === target)?.owner_name || "team member";
      toast.success(`Reassigned ${leadIds.length} lead${leadIds.length > 1 ? "s" : ""} to ${targetName}`);
      setTarget("");
      onReassigned?.();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to reassign leads");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-navy text-lg flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-gold" />
            Reassign {fromName}'s Leads
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="rounded-lg bg-muted/40 border px-4 py-3">
            <div className="text-xs text-muted-foreground">Leads to reassign</div>
            <div className="text-2xl font-bold text-navy mt-0.5">{leadIds.length}</div>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
              New assignee
            </label>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger>
                <SelectValue placeholder="Select team member..." />
              </SelectTrigger>
              <SelectContent>
                {candidates.length === 0 ? (
                  <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                    No other active team members available.
                  </div>
                ) : (
                  candidates.map(m => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="flex items-center gap-2">
                        <span className="text-sm text-navy">{m.owner_name || m.agency_email || "—"}</span>
                        <span className="text-[10px] uppercase text-muted-foreground">{m.team_role}</span>
                      </span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <p className="text-[11px] text-muted-foreground">
            All {leadIds.length} lead{leadIds.length === 1 ? "" : "s"} currently assigned to {fromName} will be transferred. This action is logged.
          </p>

          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReassign}
              disabled={!target || leadIds.length === 0 || saving}
              className="flex-1 bg-gold text-navy hover:bg-gold/90"
            >
              {saving ? "Reassigning..." : "Reassign All"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
